import React, { useEffect, useRef, useState } from 'react';

import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import { fmt_count } from '../format';
import { discard_guard_text } from '../shared/dialog_logic';
import { issue_skip_text } from './adapters';
import QaOthers from './qa_others';
import { ACTION_RESOLVE, action_verb, issue_applies, reason_error } from './params';

// "1 issue", "3 issues".
const issues_count_text = (n) => `${fmt_count(n)} ${n === 1 ? 'issue' : 'issues'}`;

/*
 * acknowledge / resolve a batch of issues. The reason is MANDATORY (every Ops action lands in
 * teacher_payment_audit with it).
 */
const IssueActionModal = ({
  show,
  action,
  rows = [],
  submitting,
  result,
  dropped_text = '',
  dropped_attempt = 0,
  server_error = '',
  on_focus_lost,
  on_close,
  on_submit
}) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  // The list reload clears the selection; the title keeps the count and verb acted on.
  const [acted_count, setActedCount] = useState(0);
  const [acted_verb, setActedVerb] = useState('');
  const result_ref = useRef(null);
  const dropped_ref = useRef(null);
  useEffect(() => {
    if (result && result_ref.current) result_ref.current.focus({ preventScroll: true });
  }, [result]);
  useEffect(() => {
    if (dropped_text && dropped_ref.current) dropped_ref.current.focus({ preventScroll: true });
  }, [dropped_text, dropped_attempt]);

  const applies = rows.filter((r) => issue_applies(r, action));
  const skip_text = issue_skip_text(
    action,
    rows.filter((r) => !issue_applies(r, action))
  );
  const error = reason_error(reason);
  const verb = action_verb(action, applies);
  const submit_off = submitting || applies.length === 0;

  const close = () => {
    setReason('');
    setTouched(false);
    on_close();
  };

  const submit = () => {
    setTouched(true);
    if (error || applies.length === 0 || submitting) return;
    setActedCount(applies.length);
    setActedVerb(verb);
    on_submit(reason);
  };

  return (
    <TpDialog
      variant="form"
      show={show}
      on_close={close}
      busy={submitting}
      on_focus_lost={on_focus_lost}
      guard_text={result ? '' : discard_guard_text(reason)}
      title={`${result ? acted_verb : verb} ${issues_count_text(result ? acted_count : applies.length)}`}
      size="md"
    >
      <div className="tp-modal tp-na-dlg">
        {result ? (
          <section aria-label="Issue action result" tabIndex={-1} ref={result_ref}>
            <div className="tp-banner tp-banner-info" role="status">
              {result.headline}
            </div>
            {result.results.map((r) => (
              <div key={r.key} className="tp-na-result" title={r.title || undefined}>
                <StatusPill tone={r.tone}>#{r.issue_id}</StatusPill> {r.text}
              </div>
            ))}
            <div className="tp-modal-ft">
              <button type="button" className="tp-btn" onClick={close}>
                Done
              </button>
            </div>
          </section>
        ) : (
          <div>
            {dropped_text ? (
              <div
                key={dropped_attempt}
                className="tp-banner tp-banner-warn"
                role="alert"
                tabIndex={-1}
                ref={dropped_ref}
              >
                {dropped_text}
              </div>
            ) : null}
            {skip_text ? <div className="tp-ctx">{skip_text}</div> : null}
            <div className="tp-na-scroll">
              {applies.map((r) => (
                <div key={r.key} className="tp-na-result">
                  <StatusPill tone={r.tier_tone}>{r.tier_label}</StatusPill> {r.label} ·{' '}
                  {r.teacher_name} ·{' '}
                  <span className={r.scope_is_id ? 'tp-mono tp-nowrap' : undefined}>
                    {r.scope_label}
                  </span>{' '}
                  · <span className="tp-nowrap">{r.amount_text}</span>
                  {/* a QA clear leaves the class's other issues open: say which */}
                  {action === ACTION_RESOLVE ? <QaOthers text={r.qa_others_text} /> : null}
                </div>
              ))}
            </div>
            <div className="tp-field">
              <label htmlFor="tp-issue-reason">
                Reason (required, recorded in the audit trail)
              </label>
              <textarea
                id="tp-issue-reason"
                className="tp-textarea"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder="What did you check, and what did you conclude?"
              />
              {touched && error ? (
                <span className="tp-neg" role="alert">
                  {error}
                </span>
              ) : null}
              {server_error ? (
                <span className="tp-neg" role="alert">
                  {server_error}
                </span>
              ) : null}
            </div>
            <div className="tp-modal-ft">
              {/* aria-disabled, not disabled: disabling the focused button drops focus. */}
              <button
                type="button"
                className="tp-btn-ghost"
                onClick={submitting ? undefined : close}
                aria-disabled={submitting ? 'true' : undefined}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tp-btn"
                onClick={submit_off ? undefined : submit}
                aria-disabled={submit_off ? 'true' : undefined}
              >
                {submitting ? 'Saving…' : `${verb} ${fmt_count(applies.length)}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </TpDialog>
  );
};

export default IssueActionModal;
